import type { AnimationInterpolation } from "@/animation/types";

// Times here are raw tick counts (number). MediaTime is a branded number, so
// callers pass branded values straight in (assignable to number) and convert
// the returned keyframe times back with mediaTime({ ticks }).

/**
 * Auto-duck (B6).
 *
 * Lowers a music/background clip under voiceover by writing a volume envelope as
 * keyframes on the clip's "volume" animation path. The existing audio mix already
 * reads keyframed volume (timeline/audio-state.ts resolveEffectiveAudioGain +
 * buildAudioGainAutomation), so the envelope applies in both preview and export
 * with no mix changes.
 *
 * P0 ducks over each voiceover clip's whole timeline span with attack/release
 * ramps. P1 can refine to actual speech regions via the cached RMS waveform
 * summary (media/waveform-summary.ts computeRmsBuckets).
 */

// dB the music drops under voiceover, and the ramp times.
export const DUCK_DB_AMOUNT = 14;
export const DUCK_ATTACK_SECONDS = 0.25;
export const DUCK_RELEASE_SECONDS = 0.6;

export interface DuckSpan {
	// Timeline time (same frame as element.startTime).
	start: number;
	end: number;
}

export interface DuckKeyframe {
	// Element-local time (relative to the music clip start), in ticks.
	time: number;
	value: number;
	interpolation: AnimationInterpolation;
}

/**
 * Build a ducking volume envelope (element-local keyframes) for a music clip,
 * given the voiceover spans (in timeline time) it should duck under.
 *
 * Returns [] when no span overlaps the clip (nothing to duck).
 */
export function computeDuckKeyframes({
	musicStart,
	musicDuration,
	voSpans,
	fullDb,
	duckDb,
	attack,
	release,
}: {
	musicStart: number;
	musicDuration: number;
	voSpans: DuckSpan[];
	fullDb: number;
	duckDb: number;
	attack: number;
	release: number;
}): DuckKeyframe[] {
	// To clip-local time, clamp to the clip, drop empties, sort, merge overlaps.
	const localSpans = voSpans
		.map((span) => ({
			start: Math.max(0, span.start - musicStart),
			end: Math.min(musicDuration, span.end - musicStart),
		}))
		.filter((span) => span.end > span.start)
		.sort((a, b) => a.start - b.start);

	const merged: Array<{ start: number; end: number }> = [];
	for (const span of localSpans) {
		const last = merged[merged.length - 1];
		if (last && span.start <= last.end) {
			last.end = Math.max(last.end, span.end);
		} else {
			merged.push({ ...span });
		}
	}
	if (merged.length === 0) {
		return [];
	}

	// Collect raw points, then sort + dedupe by time (a later point at the same
	// tick wins, so overlapping ramps collapse cleanly).
	const raw: Array<{ time: number; value: number }> = [{ time: 0, value: fullDb }];
	for (const span of merged) {
		raw.push({ time: span.start - attack, value: fullDb });
		raw.push({ time: span.start, value: duckDb });
		raw.push({ time: span.end, value: duckDb });
		raw.push({ time: span.end + release, value: fullDb });
	}
	raw.push({ time: musicDuration, value: fullDb });

	const byTime = new Map<number, number>();
	for (const point of raw) {
		const time = Math.round(Math.max(0, Math.min(musicDuration, point.time)));
		byTime.set(time, point.value);
	}

	return Array.from(byTime.entries())
		.sort((a, b) => a[0] - b[0])
		.map(([time, value]) => ({
			time,
			value,
			interpolation: "linear" as AnimationInterpolation,
		}));
}
